import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ImageLightbox from '@/components/base/ImageLightbox';
import Reveal from '@/components/base/Reveal';
import { useSeo } from '@/hooks/useSeo';
import { contents } from '@/mocks/contents';
import { SITE_ORIGIN, SITE_NAME } from '@/lib/site';
import { btn, pillClass } from '@/lib/ui';

const ContentDetail = () => {
  const { id } = useParams();
  const item = contents.find((content) => content.id === id);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!item) return [];
    const sameCategory = contents.filter(
      (content) => content.category === item.category && content.id !== item.id,
    );
    const others = contents.filter(
      (content) => content.category !== item.category && content.id !== item.id,
    );
    return [...sameCategory, ...others].slice(0, 4);
  }, [item]);

  useEffect(() => {
    setLiked(false);
    setSaved(false);
    setCopied(false);
    setLightboxOpen(false);
  }, [id]);

  useSeo({
    title: item ? `${item.title} - 在线看料高清不卡顿` : `内容详情 - ${SITE_NAME}`,
    description: item
      ? `${item.summary}（${item.categoryName}，时长 ${item.duration}，清晰度 ${item.quality}）看料社在线看料，高清不卡顿秒开。`
      : '看料社内容详情页，在线看料高清不卡顿秒开。',
    keywords: item ? `在线看料,${item.categoryName},${item.tags.join(',')},高清不卡顿` : '在线看料,内容详情',
    path: item ? `/content/${item.id}` : '/category',
    jsonLd: item
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: item.title,
            description: item.summary,
            datePublished: item.date,
            dateModified: item.date,
            inLanguage: 'zh-CN',
            articleSection: item.categoryName,
            keywords: item.tags.join(','),
            author: { '@type': 'Organization', name: item.author },
            publisher: { '@type': 'Organization', name: SITE_NAME },
            mainEntityOfPage: `${SITE_ORIGIN}/content/${item.id}`,
            image: item.cover,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_ORIGIN}/` },
              { '@type': 'ListItem', position: 2, name: '内容分类', item: `${SITE_ORIGIN}/category` },
              { '@type': 'ListItem', position: 3, name: item.title, item: `${SITE_ORIGIN}/content/${item.id}` },
            ],
          },
        ]
      : undefined,
  });

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${SITE_ORIGIN}/content/${id}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!item) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-shell flex-col items-center px-4 pb-24 pt-40 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            没有找到这条内容
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            它可能已被移动或下线。你可以回到内容分类页，看看其他正在更新的热门内容。
          </p>
          <Link to="/category" className={`${btn('primary', 'md')} mt-8`}>
            返回内容分类
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const leadParagraph = item.body[0];
  const restParagraphs = item.body.slice(1);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-shell px-4 pb-16 pt-24 md:px-8 md:pb-20 md:pt-32">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs font-label text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm" />
          </span>
          <Link to="/category" className="transition-colors hover:text-primary-600">
            内容分类
          </Link>
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm" />
          </span>
          <Link to={`/category?category=${item.category}`} className="transition-colors hover:text-primary-600">
            {item.categoryName}
          </Link>
        </nav>

        <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-12">
          <article className="lg:col-span-8">
            <span className={`${pillClass} bg-primary-100 text-primary-700`}>{item.categoryName}</span>
            <h1 className="mt-3 font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-4xl md:leading-tight">
              {item.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-label text-foreground-500 md:text-sm">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-user-3-line text-sm" />
                </span>
                {item.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-calendar-line text-sm" />
                </span>
                {item.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-play-circle-line text-sm" />
                {item.views} 次观看
              </span>
              <span className="rounded-md bg-accent-100 px-2 py-0.5 text-accent-800">{item.quality}</span>
            </div>

            <div className="relative mt-6 h-56 w-full overflow-hidden rounded-lg md:h-[420px]">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 在线看料高清`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent" />
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                aria-label="查看高清剧照"
                className="absolute inset-0 flex items-center justify-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition-transform duration-300 hover:scale-105">
                  <i className="ri-fullscreen-line text-2xl" />
                </span>
              </button>
              <span className="absolute bottom-4 left-4 rounded-md bg-foreground-950/70 px-2.5 py-1 text-xs font-label text-background-50">
                {item.duration}
              </span>
              <span className="absolute bottom-4 right-4 rounded-md bg-background-50/90 px-2.5 py-1 text-xs font-label text-foreground-800">
                点击查看高清剧照
              </span>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-foreground-500">
              提示：点击画面可放大查看清晰画面细节，支持按 Esc 或点击空白处退出预览。
            </p>

            <div className="mt-8 border-t border-background-200 pt-8">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-2xl">内容看点</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{leadParagraph}</p>

              <h2 className="mt-8 font-heading text-lg font-semibold text-foreground-950 md:text-2xl">现场细节</h2>
              <div className="mt-4 flex flex-col gap-4">
                {restParagraphs.map((paragraph, index) => (
                  <p key={index} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              {item.tags.map((tag) => (
                <span key={tag} className={`${pillClass} bg-secondary-100 text-secondary-900`}>
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-price-tag-3-line text-xs" />
                  </span>
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setLiked((prev) => !prev)}
                aria-pressed={liked}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-label transition-colors ${
                  liked
                    ? 'border-primary-500 bg-primary-100 text-primary-700'
                    : 'border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                <i className={liked ? 'ri-heart-3-fill text-base' : 'ri-heart-3-line text-base'} />
                {liked ? '已点赞' : '点赞'} · {item.likes}
              </button>
              <button
                type="button"
                onClick={() => setSaved((prev) => !prev)}
                aria-pressed={saved}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-label transition-colors ${
                  saved
                    ? 'border-secondary-500 bg-secondary-100 text-secondary-900'
                    : 'border-background-300 text-foreground-700 hover:border-secondary-400 hover:text-secondary-800'
                }`}
              >
                <i className={saved ? 'ri-bookmark-fill text-base' : 'ri-bookmark-line text-base'} />
                {saved ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-label text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                <i className="ri-link text-base" />
                {copied ? '链接已复制' : '复制链接'}
              </button>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="flex flex-col gap-6 lg:sticky lg:top-24">
              <Reveal>
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">内容信息</h2>
                  <dl className="mt-4 flex flex-col gap-3 text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">所属分类</dt>
                      <dd className="text-foreground-900">{item.categoryName}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">时长</dt>
                      <dd className="text-foreground-900">{item.duration}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">清晰度</dt>
                      <dd className="text-foreground-900">{item.quality}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">实时热度</dt>
                      <dd className="text-primary-600">{item.hot}</dd>
                    </div>
                  </dl>
                  <Link to={`/category?category=${item.category}`} className={`${btn('outline', 'sm')} mt-5 w-full`}>
                    浏览同分类内容
                  </Link>
                </div>
              </Reveal>

              <div>
                <h2 className="font-heading text-base font-semibold text-foreground-950 md:text-lg">相关内容推荐</h2>
                <div className="mt-4 flex flex-col gap-4">
                  {related.map((relatedItem) => (
                    <Reveal key={relatedItem.id}>
                      <article className="group flex gap-3">
                        <Link
                          to={`/content/${relatedItem.id}`}
                          className="h-16 w-24 shrink-0 overflow-hidden rounded-md"
                          aria-label={relatedItem.title}
                        >
                          <img
                            src={relatedItem.cover}
                            alt={relatedItem.title}
                            title={`${relatedItem.title} - 在线看料`}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </Link>
                        <div className="min-w-0 flex-1">
                          <h3 className="line-clamp-2 font-heading text-sm font-medium leading-snug text-foreground-950">
                            <Link to={`/content/${relatedItem.id}`} className="transition-colors hover:text-primary-600">
                              {relatedItem.title}
                            </Link>
                          </h3>
                          <div className="mt-2 flex items-center gap-3 text-[11px] font-label text-foreground-500">
                            <span>{relatedItem.categoryName}</span>
                            <span className="inline-flex items-center gap-1">
                              <i className="ri-play-circle-line text-sm" />
                              {relatedItem.views}
                            </span>
                          </div>
                        </div>
                      </article>
                    </Reveal>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-secondary-100 p-5">
                <h2 className="font-heading text-base font-semibold text-secondary-900">订阅最新热料</h2>
                <p className="mt-2 text-xs leading-relaxed text-secondary-800">
                  每周精选热门现场与话题解析，一封邮件看完，随时可以退订。
                </p>
                <Link to="/about#contact" className={`${btn('primary', 'sm')} mt-4 w-full`}>
                  前往订阅
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <ImageLightbox
        open={lightboxOpen}
        src={item.cover}
        caption={`${item.title} · ${item.categoryName} · ${item.quality}`}
        onClose={() => setLightboxOpen(false)}
      />
    </SiteLayout>
  );
};

export default ContentDetail;